import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Copy,
  Crown,
  Mail,
  MailWarning,
  Trash2,
  UserMinus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { ShellContext } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { teamsApi, users } from '@/lib/api/endpoints';
import type { Invitation, Team, TeamMember } from '@/lib/api/types';
import { describeFailure } from '@/lib/i18n/errors';
import { useT } from '@/lib/i18n';
import { Page } from '@/routes/Today';

/**
 * Who is us, and how somebody becomes us.
 *
 * The screen is arranged around the two facts that are easy to get wrong and
 * expensive when you do: a team has exactly one manager, and an invitation is
 * a door that closes on its own. Both are stated on the page rather than left
 * for the reader to infer from what the buttons happen to allow.
 */
export function TeamsRoute() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { currentUser } = useOutletContext<ShellContext>();
  const [name, setName] = useState('');

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.list(),
  });

  const create = useMutation({
    mutationFn: () => teamsApi.create({ name }),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );

  // Creating a team is the administrator's alone. Read off the account, not
  // inferred from the teams on screen: a member with no team has none to infer
  // from, and used to be shown a form the server would refuse.
  const canCreate = currentUser.is_superadmin;

  return (
    <Page>
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">{t('teams.title')}</h1>
        <p className="mt-1 text-sm text-content-muted">{t('teams.subtitle')}</p>
      </header>

      {canCreate ? (
        <form
          className="mb-6 flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('teams.namePlaceholder')}
            className="min-w-48 flex-1 rounded-lg bg-surface px-3 py-2 text-sm text-content ring-1 ring-border placeholder:text-content-subtle focus:outline-none focus:ring-accent"
          />
          <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
            {t('teams.add')}
          </Button>
        </form>
      ) : null}

      {data.length === 0 ? (
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title={t('teams.empty.title')}
          // Two different situations, and telling them apart is the whole
          // value of the screen for somebody who is not an administrator:
          // "set one up" versus "somebody has to put you in one".
          hint={canCreate ? t('teams.empty.hint') : t('teams.empty.notAdmin')}
        />
      ) : (
        <ul className="space-y-2">
          {data.map((team) => (
            <TeamCard key={team.id} team={team} />
          ))}
        </ul>
      )}
    </Page>
  );
}

function TeamCard({ team }: { team: Team }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: members } = useQuery({
    queryKey: ['teams', team.id, 'members'],
    queryFn: () => teamsApi.members(team.id),
    enabled: open,
  });
  const { data: invitations } = useQuery({
    queryKey: ['invitations', team.id],
    queryFn: () => teamsApi.invitations(team.id),
    enabled: open && team.can_invite,
  });

  const remove = useMutation({
    mutationFn: () => teamsApi.remove(team.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['teams'] }),
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  return (
    <li className="rounded-xl border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <Users className="h-4 w-4 shrink-0 text-content-subtle" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-content">
            {team.name}
          </span>
          <span className="block text-xs text-content-subtle">
            {/* The manager is the fact worth putting on the collapsed row:
                "exactly one" is only reassuring if you can see who. */}
            {team.manager_name
              ? t('teams.managedBy', { name: team.manager_name })
              : t('teams.noManager')}
            {' · '}
            {t('teams.memberCount', { count: team.member_count })}
          </span>
        </span>
      </button>

      {open ? (
        <div className="border-t border-border px-4 py-3">
          <MemberList team={team} members={members ?? []} />

          {team.can_invite ? (
            <>
              <AddExistingMember team={team} />
              <InviteForm team={team} invitations={invitations ?? []} />
            </>
          ) : null}

          {team.can_manage ? (
            <div className="mt-4 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (window.confirm(t('teams.deleteConfirm', { name: team.name }))) {
                    remove.mutate();
                  }
                }}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {t('teams.delete')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function MemberList({ team, members }: { team: Team; members: TeamMember[] }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['teams'] });
  };
  const setRole = useMutation({
    mutationFn: (input: { userId: string; role: 'MANAGER' | 'MEMBER' }) =>
      teamsApi.setRole(team.id, input.userId, input.role),
    onSuccess: invalidate,
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });
  const drop = useMutation({
    mutationFn: (userId: string) => teamsApi.removeMember(team.id, userId),
    onSuccess: invalidate,
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  if (members.length === 0) {
    return <p className="text-xs text-content-subtle">{t('teams.noMembers')}</p>;
  }

  return (
    <ul className="space-y-1">
      {members.map((member) => (
        <li key={member.user_id} className="flex items-center gap-2 py-1">
          {member.role === 'MANAGER' ? (
            <Crown className="h-3.5 w-3.5 shrink-0 text-amber" aria-hidden />
          ) : (
            <span className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="min-w-0 flex-1 truncate text-sm text-content">
            {member.display_name}
            <span className="ml-1.5 text-xs text-content-subtle">
              @{member.username}
            </span>
          </span>
          {team.can_manage ? (
            <>
              {member.role === 'MEMBER' ? (
                <button
                  type="button"
                  onClick={() =>
                    setRole.mutate({ userId: member.user_id, role: 'MANAGER' })
                  }
                  className="rounded px-2 py-0.5 text-xs text-content-muted hover:bg-surface-hover hover:text-content"
                  title={t('teams.makeManagerHint')}
                >
                  {t('teams.makeManager')}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => drop.mutate(member.user_id)}
                className="rounded p-1 text-content-subtle hover:bg-surface-hover hover:text-content"
                title={t('teams.removeMember')}
              >
                <UserMinus className="h-3.5 w-3.5" aria-hidden />
              </button>
            </>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Put somebody who already has an account into the team.
 *
 * An invitation cannot reach them: it is refused for an address that already
 * has an account in this team, and redeeming one is the only other way a
 * membership is created. So a team that loses a member had no way to get them
 * back — which is also why a manager is not allowed to evict the
 * administrator.
 *
 * The search is the ordinary teammate-scoped one, which for the platform
 * administrator returns every account: they build the teams, so they have to
 * be able to find the person to put in one.
 */
function AddExistingMember({ team }: { team: Team }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');

  const { data: found } = useQuery({
    queryKey: ['users', 'search', term],
    queryFn: () => users.search(term),
    enabled: term.trim().length >= 2,
  });

  const add = useMutation({
    mutationFn: (userId: string) => teamsApi.addMember(team.id, userId),
    onSuccess: () => {
      setTerm('');
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  return (
    <div className="mt-3">
      <label className="block">
        <span className="mb-1 block text-xs text-content-muted">
          {t('teams.addExisting')}
        </span>
        <input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={t('teams.addExistingPlaceholder')}
          className="w-full rounded-lg bg-surface px-3 py-2 text-sm text-content ring-1 ring-border placeholder:text-content-subtle focus:outline-none focus:ring-accent"
        />
      </label>
      {found && found.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {found.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                onClick={() => add.mutate(person.id)}
                disabled={add.isPending}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-surface-hover"
              >
                <span className="min-w-0 flex-1 truncate text-content">
                  {person.display_name}
                  <span className="ml-1.5 text-xs text-content-subtle">
                    @{person.username}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-accent">
                  {t('teams.addToTeam')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function InviteForm({ team, invitations }: { team: Team; invitations: Invitation[] }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(7);
  const [asManager, setAsManager] = useState(false);
  const [link, setLink] = useState<{ url: string; failed: string | null } | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      teamsApi.invite({
        email: email.trim(),
        team_id: team.id,
        role: asManager ? 'MANAGER' : 'MEMBER',
        ttl_days: days,
      }),
    onSuccess: (created) => {
      setEmail('');
      // The link is shown whether or not the email went out: the token is
      // never readable again, so a silent success would strand the invitee if
      // delivery quietly failed.
      setLink({ url: created.accept_url, failed: created.delivery_error ?? null });
      void queryClient.invalidateQueries({ queryKey: ['invitations', team.id] });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => teamsApi.revokeInvitation(id),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['invitations', team.id] }),
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  const pending = invitations.filter((row) => row.state === 'PENDING');

  return (
    <div className="mt-4 border-t border-border pt-3">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim()) invite.mutate();
        }}
      >
        <label className="min-w-48 flex-1">
          <span className="mb-1 block text-xs text-content-muted">
            {t('invite.email')}
          </span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="kolega@firma.cz"
            className="w-full rounded-lg bg-surface px-3 py-2 text-sm text-content ring-1 ring-border placeholder:text-content-subtle focus:outline-none focus:ring-accent"
          />
        </label>
        <label className="w-28">
          <span className="mb-1 block text-xs text-content-muted">
            {t('invite.validity')}
          </span>
          <input
            type="number"
            min={1}
            max={90}
            value={days}
            onChange={(event) => setDays(Number(event.target.value) || 7)}
            className="w-full rounded-lg bg-surface px-3 py-2 text-sm tabular-nums text-content ring-1 ring-border focus:outline-none focus:ring-accent"
          />
        </label>
        <Button type="submit" size="sm" disabled={!email.trim() || invite.isPending}>
          <Mail className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {t('invite.send')}
        </Button>
      </form>

      {/* Only the administrator may appoint a manager, and only when the chair
          is empty — the server refuses otherwise, so the box is not offered. */}
      {team.can_manage && !team.manager_user_id ? (
        <label className="mt-2 flex items-center gap-2 text-xs text-content-muted">
          <input
            type="checkbox"
            checked={asManager}
            onChange={(event) => setAsManager(event.target.checked)}
            className="rounded border-border"
          />
          {t('invite.asManager')}
        </label>
      ) : null}

      {link ? <IssuedLink link={link} /> : null}

      {pending.length > 0 ? (
        <ul className="mt-3 space-y-1">
          {pending.map((row) => (
            <li key={row.id} className="flex items-center gap-2 text-xs">
              {row.emailed ? (
                <Mail className="h-3.5 w-3.5 shrink-0 text-content-subtle" aria-hidden />
              ) : (
                <MailWarning
                  className="h-3.5 w-3.5 shrink-0 text-amber"
                  aria-hidden
                  // Said, not implied: an invitation nobody delivered looks
                  // exactly like one nobody answered.
                />
              )}
              <span className="min-w-0 flex-1 truncate text-content-muted">
                {row.email}
                {row.role === 'MANAGER' ? ` · ${t('teams.manager')}` : ''}
              </span>
              <span className="shrink-0 text-content-subtle">
                {t('invite.expires', { date: formatDay(row.expires_at) })}
              </span>
              <button
                type="button"
                onClick={() => revoke.mutate(row.id)}
                className="shrink-0 rounded px-1.5 py-0.5 text-content-subtle hover:bg-surface-hover hover:text-content"
              >
                {t('invite.revoke')}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function IssuedLink({ link }: { link: { url: string; failed: string | null } }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-3 rounded-lg border border-border bg-surface-hover px-3 py-2">
      <p className="text-xs text-content-muted">
        {link.failed ? t('invite.notEmailed') : t('invite.emailed')}
      </p>
      <div className="mt-1.5 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-surface px-2 py-1 text-[11px] text-content">
          {link.url}
        </code>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(link.url);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
        </Button>
      </div>
      <p className="mt-1.5 text-[11px] text-content-subtle">{t('invite.shownOnce')}</p>
    </div>
  );
}

function formatDay(iso: string): string {
  const date = new Date(iso);
  return `${date.getDate()}. ${date.getMonth() + 1}. ${date.getFullYear()}`;
}
